import { useMemo, useState } from "react";
import { Avatar, Box, Menu, MenuItem, Typography } from "@mui/material";
import { useCustomAuth } from "../contexts/AuthContext";
import { ReactComponent as AdminIcon } from "../assets/img/user/Admin.svg";
import { ReactComponent as UserIcon } from "../assets/img/user/User.svg";
import { ReactComponent as LogOutIcon } from "../assets/img/user/LogOut.svg";
import { stringToColor } from "../utils/avatarUtils";

export const UserProfile = ({ handleLogout, userName }) => {
  // State for user menu
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Auth state
  const { userData, isAdminMode, setIsAdminMode } = useCustomAuth();
  const isAdmin = userData?.roles.includes("ADMIN") || userData?.roles.includes("ROOT") || false;

  // Handle user menu open
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle user menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle logout click
  const onLogout = () => {
    handleMenuClose();
    handleLogout();
  };

  // Generate avatar properties
  const avatarProps = useMemo(() => {
    return {
      sx: {
        bgcolor: stringToColor(userName),
        width: 36,
        height: 36,
        cursor: "pointer",
      },
      children: userName.charAt(0).toUpperCase(),
    };
  }, [userName]);

  return (
    <Box sx={{ flexBasis: "250px", display: "flex", justifyContent: "flex-end" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          padding: "4px 8px",
          borderRadius: "20px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
        onClick={handleMenuOpen}
      >
        <Typography variant="body1" sx={{ mr: 1.5 }}>
          {userName}
        </Typography>
        <Avatar {...avatarProps} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            mt: 1,
            width: 160,
            borderRadius: "8px",
            "& .MuiMenuItem-root": {
              px: 2,
              py: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
      >
        <Box sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)", py: 1.5, px: 2, display: "flex", alignItems: "center" }}>
          <Avatar
            sx={{ width: 30, height: 30, mr: 1.5, bgcolor: stringToColor(userName) }}
          >
            {userName.charAt(0).toUpperCase()}
          </Avatar>
          <Typography>Hi {userName.split(" ")[0]}!</Typography>
        </Box>
        {isAdmin
          ? isAdminMode
            ? (
              <MenuItem onClick={() => setIsAdminMode((prev) => !prev)} sx={{ py: 1.5 }}>
                <Box component={UserIcon} sx={{ width: 20, height: 20, mr: 1 }} />
                <Typography>User Mode</Typography>
              </MenuItem>
            )
            : (
              <MenuItem onClick={() => setIsAdminMode((prev) => !prev)} sx={{ py: 1.5 }}>
                <Box component={AdminIcon} sx={{ width: 20, height: 20, mr: 1 }} />
                <Typography>Admin Mode</Typography>
              </MenuItem>
            )
          : null}
        <MenuItem onClick={onLogout} sx={{ py: 1.5 }}>
          <Box component={LogOutIcon} sx={{ width: 20, height: 20, mr: 1 }} />
          <Typography>Log Out</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};
